import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Building2, LogIn } from "lucide-react";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CONFIG = {
  logoUrl: "",
  logoHeight: 48,
  heading: "Client Portal",
  subheading: "Enter your credentials to access the catalog",
  showInfoBox: true,
  infoBoxText: "Contact your account manager for login credentials",
  infoBoxBgColor: "#f1f5f9",
  infoBoxTextColor: "#64748b"
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/login`);
        setConfig({ ...DEFAULT_CONFIG, ...response.data });
      } catch (error) {
        console.error("Error fetching login config:", error);
      }
    };
    fetchConfig();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.token, response.data.user);
      toast.success("Login successful!");
      
      if (response.data.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/catalog");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center px-6 py-12" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Card className="w-full max-w-md p-8 border-border/50 shadow-lg" data-testid="login-card">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {config.logoUrl ? (
                <img 
                  src={config.logoUrl} 
                  alt="Logo" 
                  style={{ height: `${config.logoHeight}px` }}
                  className="object-contain"
                />
              ) : (
                <div className="p-3 bg-primary/10 rounded-sm">
                  <Building2 className="h-12 w-12 text-primary" />
                </div>
              )}
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope' }} data-testid="login-heading">
              {config.heading}
            </h1>
            <p className="text-muted-foreground">{config.subheading}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="client@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full gap-2 h-12"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                "Logging in..."
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Login
                </>
              )}
            </Button>
          </form>

          {config.showInfoBox && config.infoBoxText && (
            <div 
              className="mt-8 p-4 rounded-sm border border-border/50"
              style={{ 
                backgroundColor: config.infoBoxBgColor,
                color: config.infoBoxTextColor 
              }}
            >
              <p className="text-sm text-center" dangerouslySetInnerHTML={{ __html: config.infoBoxText.replace(/\n/g, '<br/>') }} />
            </div>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
}
